import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Select, Input, TextArea, toast } from '../../components/common';
import { budgetApi } from '../../services/api';

export default function BudgetTransferForm({ onSuccess, onCancel }) {
    const [form, setForm] = useState({
        fromBudgetId: '',
        toBudgetId: '',
        amount: '',
        reason: ''
    });
    const [errors, setErrors] = useState({});

    const { data: budgets } = useQuery({
        queryKey: ['budgets', 'active'],
        queryFn: () => budgetApi.list({ status: 'active' }),
        select: (res) => res.data?.data || []
    });

    const mutation = useMutation({
        mutationFn: (data) => budgetApi.requestTransfer(data),
        onSuccess: () => {
            toast.success('Transfer request submitted successfully');
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to submit transfer');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.fromBudgetId) newErrors.fromBudgetId = 'Source budget is required';
        if (!form.toBudgetId) newErrors.toBudgetId = 'Target budget is required';
        if (form.fromBudgetId === form.toBudgetId) newErrors.toBudgetId = 'Must be different from source';
        if (!form.amount || parseFloat(form.amount) <= 0) newErrors.amount = 'Valid amount is required';
        if (!form.reason) newErrors.reason = 'Reason is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate({
            fromBudgetId: form.fromBudgetId,
            toBudgetId: form.toBudgetId,
            amount: parseFloat(form.amount),
            reason: form.reason
        });
    };

    const budgetOptions = budgets?.map(b => ({
        value: b.id.toString(),
        label: `${b.budgetCode} - ${b.department?.name}`
    })) || [];

    return (
        <form onSubmit={handleSubmit} className="transfer-form">
            <div className="form-stack">
                <Select
                    name="fromBudgetId"
                    label="From Budget"
                    placeholder="Select source budget"
                    value={form.fromBudgetId}
                    onChange={handleChange}
                    options={budgetOptions}
                    error={errors.fromBudgetId}
                    required
                />
                <Select
                    name="toBudgetId"
                    label="To Budget"
                    placeholder="Select target budget"
                    value={form.toBudgetId}
                    onChange={handleChange}
                    options={budgetOptions.filter(b => b.value !== form.fromBudgetId)}
                    error={errors.toBudgetId}
                    required
                />
                <Input
                    name="amount"
                    type="number"
                    label="Transfer Amount"
                    placeholder="Enter amount to transfer"
                    value={form.amount}
                    onChange={handleChange}
                    error={errors.amount}
                    required
                />
                <TextArea
                    name="reason"
                    label="Reason"
                    placeholder="Explain the reason for this transfer"
                    value={form.reason}
                    onChange={handleChange}
                    error={errors.reason}
                    rows={3}
                    required
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    Submit Transfer Request
                </Button>
            </div>
        </form>
    );
}

BudgetTransferForm.propTypes = {
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
